import { logger } from '@luna/logger';
import { createSingleton } from '@luna/utils';

import type * as Model from './models/models';
import type { NativeBridge } from './models/nativeBridge';

const REPLY_TIMEOUT = 5000;

export interface AndroidBus {
    onmessage: (event: { data: string }) => void;
    postMessage: (data: string) => void;
}

export interface IosBus {
    messageHandlers: {
        iosVanillaBridge: {
            postMessage: (data: string) => void;
        };
    };
}

export interface BusProvider {
    androidVanillaBridge?: AndroidBus;
    webkit?: IosBus;
    jsVanillaBridge?: { postMessage: (data: string) => void };
    isVanillaDevBridge?: boolean;
}

type Listener = (event: Model.Event) => void;

function isIos(bus: AndroidBus | IosBus): bus is IosBus {
    return 'messageHandlers' in bus && typeof (bus as IosBus).messageHandlers === 'object';
}

function isReply(event: Model.Event): event is Model.Reply {
    return 'requestId' in event && ('error' in event || 'data' in event);
}

function jsonOrNull<T = unknown>(data: string): T | null {
    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}

type Deferred<T> = Promise<T> & { resolve: (data: T) => void; reject: (error: Error) => void };

function createDeferred<T>(): Deferred<T> {
    const context: Pick<Deferred<T>, 'resolve' | 'reject'> = {
        resolve: () => {},
        reject: () => {},
    };

    const promise = new Promise((resolve, reject) => {
        context.resolve = resolve;
        context.reject = reject;
    });

    const deferred = promise as Deferred<T>;
    deferred.resolve = context.resolve;
    deferred.reject = context.reject;

    return deferred;
}

function createBridge(bus: AndroidBus | IosBus): NativeBridge {
    const listeners = new Set<Listener>();

    function addListener(listener: Listener): void {
        listeners.add(listener);
    }

    function removeListener(listener: Listener): void {
        listeners.delete(listener);
    }

    function nativePostMessage(data: string): void {
        logger.debug('[NativeBridge]: sending', data, jsonOrNull(data));

        if (isIos(bus)) {
            bus.messageHandlers.iosVanillaBridge.postMessage(data);
        } else {
            bus.postMessage(data);
        }
    }

    const onMessage = (data: string): void => {
        logger.debug('[NativeBridge]: received', data, jsonOrNull(data));

        const event = JSON.parse(data) as Model.Event;
        listeners.forEach((listener) => listener(event));
    };

    if (isIos(bus)) {
        (window as BusProvider).jsVanillaBridge = { postMessage: onMessage };
    } else {
        (bus as AndroidBus).onmessage = (event) => onMessage(event.data);
    }

    return {
        post(event) {
            nativePostMessage(JSON.stringify(event));
        },
        exec(request) {
            const deferred = createDeferred();

            const listener = (event: Model.Event) => {
                if (isReply(event) && event.requestId === request.requestId) {
                    removeListener(listener);

                    if (event.error) {
                        deferred.reject(new Error(event.error.message));
                        return;
                    }

                    deferred.resolve(event);

                    return;
                }

                throw new Error('Could not extract reply, check the payload of the message');
            };
            addListener(listener);

            nativePostMessage(JSON.stringify(request));

            const timeoutPromise = new Promise((_, reject) => {
                if ((window as BusProvider).isVanillaDevBridge) {
                    return;
                }

                setTimeout(() => {
                    reject(new Error('Timeout'));
                }, REPLY_TIMEOUT);
            });

            return Promise.race([deferred, timeoutPromise]).finally(() =>
                removeListener(listener),
            ) as Promise<any>;
        },
    };
}

const nativeBridgeSingleton = createSingleton<NativeBridge | null>(null);

export function clearNativeBridgeSingleton(): void {
    nativeBridgeSingleton.set(null);
}

function getIosBus(): IosBus | undefined {
    if ((window as BusProvider).webkit?.messageHandlers?.iosVanillaBridge) {
        return window.webkit as IosBus;
    }

    return undefined;
}

function getAndroidBus(): AndroidBus | undefined {
    return (window as BusProvider).androidVanillaBridge;
}

export function hasIosBus(): boolean {
    return !!getIosBus();
}

export function hasAndroidBus(): boolean {
    return !!getAndroidBus();
}

export function getNativeBridge(): NativeBridge | undefined {
    const singleton = nativeBridgeSingleton.get();

    if (singleton) {
        return singleton;
    }

    const iosBus = getIosBus();

    if (iosBus) {
        const nativeBridge = createBridge(iosBus);
        nativeBridgeSingleton.set(nativeBridge);
        return nativeBridge;
    }

    const androidBus = getAndroidBus();

    if (androidBus) {
        const nativeBridge = createBridge(androidBus);
        nativeBridgeSingleton.set(nativeBridge);
        return nativeBridge;
    }

    return undefined;
}
